import { fetcher, SWRFetcher } from '@commenda/utils';
import useSWR from 'swr';
import {
  AcceptInvitationResponse,
  ActivateInvitationResponse,
  CreateInvitationResponse,
  CustomerSellerRelationship,
  CustomerSellerRelationshipByIdResponse,
  SellerCustomerRelationshipListResponse,
} from './types';

// Customer APIs
export async function getExemptionPortalCertificateUploadUrl(
  relationshipId: string,
  fileName: string,
  clerkToken: string
): Promise<{
  signedUrl: string;
  hostedUrl: string;
  signedGetUrl: string;
} | null> {
  try {
    const response = await fetcher(
      `portal/customer/relationships/${relationshipId}/upload-url`,
      {
        method: 'POST',
        body: { fileName },
      },
      undefined,
      clerkToken
    );

    if (!response.ok) {
      console.error(await response.json());
      return null;
    }

    return await response.json();
  } catch (error) {
    console.error(error);
    return null;
  }
}

// Seller APIs
export async function useCreateExemptionPortalCustomerInvitation(
  corporationId: number,
  customerId: string,
  clerkToken: string
): Promise<{
  success: boolean;
  data?: CreateInvitationResponse;
  error?: string;
}> {
  try {
    const response = await fetcher(
      `portal/sellers/${corporationId}/invitations`,
      {
        method: 'POST',
        body: {
          customerId,
        },
      },
      undefined,
      clerkToken
    );

    const data = await response.json();

    if (!response.ok) {
      return {
        success: false,
        error: data.message || 'Failed to create invitation',
      };
    }

    return {
      success: true,
      data: data as CreateInvitationResponse,
    };
  } catch (error) {
    return {
      success: false,
      error:
        error instanceof Error ? error.message : 'An unexpected error occurred',
    };
  }
}

export function useExemptionPortalSellerRelationships({
  corporationId,
  limit = 10,
  cursor = null,
  searchTerm = null,
}: {
  corporationId: number;
  limit?: number;
  cursor?: string | null;
  searchTerm?: string | null;
}) {
  const query = new URLSearchParams();

  if (limit) {
    query.append('limit', limit.toString());
  }
  if (cursor) {
    query.append('cursor', cursor);
  }
  if (searchTerm) {
    query.append('search_term', searchTerm);
  }

  const { data, error, isLoading, mutate } =
    useSWR<SellerCustomerRelationshipListResponse>(
      corporationId
        ? `portal/sellers/${corporationId}/customers?${query}`
        : null,
      SWRFetcher({ method: 'GET' }, undefined)
    );

  return {
    data: {
      customers: data?.data.customers,
      nextCursor: data?.data.cursor,
      totalCustomers: data?.data.total_customers,
    },
    isLoading,
    error,
    mutate,
  };
}

// Customer APIs
export async function useAcceptExemptionPortalInvitation(
  requestId: string,
  clerkToken: string
): Promise<{
  success: boolean;
  data?: AcceptInvitationResponse;
  error?: string;
}> {
  try {
    const response = await fetcher(
      `portal/invitations/${requestId}/accept`,
      {
        method: 'POST',
      },
      undefined,
      clerkToken
    );

    const data = await response.json();

    if (!response.ok) {
      return {
        success: false,
        error: data.message || 'Failed to accept invitation',
      };
    }

    return {
      success: true,
      data: data as AcceptInvitationResponse,
    };
  } catch (error) {
    return {
      success: false,
      error:
        error instanceof Error ? error.message : 'An unexpected error occurred',
    };
  }
}

// Customer APIs
export async function useVerifyExemptionPortalInvitation(
  requestId: string,
  email: string,
  clerkToken: string
): Promise<{
  success: boolean;
  data?: ActivateInvitationResponse;
  error?: string;
}> {
  try {
    const response = await fetcher(
      `portal/invitations/${requestId}/activate?email=${email}`,
      {
        method: 'GET',
      },
      undefined,
      clerkToken
    );

    const data = await response.json();

    if (!response.ok) {
      return {
        success: false,
        error: data.message || 'Failed to verify invitation',
      };
    }

    return {
      success: true,
      data: data as ActivateInvitationResponse,
    };
  } catch (error) {
    return {
      success: false,
      error:
        error instanceof Error ? error.message : 'An unexpected error occurred',
    };
  }
}

export function useExemptionPortalCustomerRelationships(clerkToken: string): {
  relationships: CustomerSellerRelationship[] | undefined;
  isLoading: boolean;
  error: Error | null;
  mutate: (
    data?: CustomerSellerRelationship[]
  ) => Promise<CustomerSellerRelationship[] | undefined>;
} {
  const { data, error, isLoading, mutate } = useSWR<
    CustomerSellerRelationship[]
  >(
    clerkToken ? 'portal/customer/relationships' : null,
    clerkToken ? SWRFetcher({ method: 'GET' }, undefined, clerkToken) : null
  );

  return {
    relationships: data,
    isLoading,
    error,
    mutate,
  };
}

export async function useExemptionPortalCustomerRelationshipById(
  relationshipId: string,
  clerkToken: string
): Promise<{
  relationship: CustomerSellerRelationshipByIdResponse | undefined;
  isLoading: boolean;
  error: Error | null;
  mutate: (
    data?: CustomerSellerRelationshipByIdResponse
  ) => Promise<CustomerSellerRelationshipByIdResponse | undefined>;
}> {
  const { data, error, isLoading, mutate } =
    useSWR<CustomerSellerRelationshipByIdResponse>(
      relationshipId ? `portal/customer/relationships/${relationshipId}` : null,
      SWRFetcher({ method: 'GET' }, undefined, clerkToken)
    );

  return {
    relationship: data,
    isLoading,
    error,
    mutate,
  };
}
